import { data, type ObservableValue } from '@amazon/vinyl-observable'

export const RETRY_DEBOUNCE_MS = 1000
export const RETRY_MAX_COOLDOWN_MS = 30000

/** Cooldown after the `failures`-th failed attempt in a row: 1 s, 2 s, 4 s, 8 s, up to 30 s. */
export function retryCooldownMs(failures: number): number {
    return Math.min(RETRY_DEBOUNCE_MS * 2 ** (failures - 1), RETRY_MAX_COOLDOWN_MS)
}

export interface RetryGuardOptions {
    /**
     * True while the section the attempt reloads is in its failed state. An attempt counts as failed
     * when it rejects or leaves this true once it settles; one that leaves it false resets the
     * backoff, and so does a press that finds it false. Without it, every press counts as a
     * failure, since nothing reports that the section recovered.
     */
    readonly failed$?: ObservableValue<boolean>
}

export interface RetryGuard {
    /** True from a press until its attempt settles and the cooldown after it has passed. */
    readonly pending$: ObservableValue<boolean>
    /** Starts `attempt` unless an earlier press is still pending. */
    readonly press: () => void
    /**
     * `press` for a reconnect: while pending, waits and presses once when the guard frees, if the
     * section is still failed. Any number of calls while pending make one press.
     */
    readonly pressWhenIdle: () => void
}

/**
 * One press starts one attempt. Later presses, manual or from a reconnect, do nothing until the
 * attempt (when it returns a promise) has settled and the cooldown has passed: `RETRY_DEBOUNCE_MS`
 * after a success, and after the n-th failure in a row `retryCooldownMs(n)`. Each guard keeps its
 * own backoff, so sections back off independently.
 */
export function retryGuard(
    attempt: () => void | Promise<unknown>,
    options: RetryGuardOptions = {}
): RetryGuard {
    const { failed$ } = options
    const pending$ = data(false)
    let failures = 0
    let queued = false

    function free() {
        pending$.value = false
        if (!queued) return
        queued = false
        if (!failed$ || failed$.value) press()
    }

    function press() {
        if (pending$.value) return
        if (failed$ && !failed$.value) failures = 0
        pending$.value = true
        let holds = 2
        const release = () => {
            if (--holds === 0) free()
        }
        setTimeout(release, RETRY_DEBOUNCE_MS)
        const settle = (rejected: boolean) => {
            if (rejected || !failed$ || failed$.value) {
                failures++
                setTimeout(release, retryCooldownMs(failures))
            } else {
                failures = 0
                release()
            }
        }
        try {
            Promise.resolve(attempt()).then(
                () => settle(false),
                () => settle(true)
            )
        } catch (error) {
            settle(true)
            throw error
        }
    }

    function pressWhenIdle() {
        if (pending$.value) queued = true
        else press()
    }

    return { pending$, press, pressWhenIdle }
}
